import {Injectable, OnDestroy} from "@angular/core";
import {Observable, Subject, Subscriber, Subscription} from "rxjs";
import {SessionModel} from "../orchestration/session.model";
import {OrderingContextModel} from "../ordering-context/ordering-context.model";
import {OrchestrationEvents} from "../orchestration/orchestration-events";
import {MenuCategorySelection, MenuEvents, MenuItemSelection} from "./menu-events";
import {OrderingContextEvents} from "../ordering-context/ordering-context-events";
import {OrderModel} from "../order/order.model";
import {EntityData, FedeltaImageData} from "@fedelta-pos/data-client";
import {Color} from "../ui-elements/color";
import {CssVarHelper} from "../html-utils/css-var.helper";
import {PromoModel} from "../promo/promo.model";
import {ResponsiveService} from "../orchestration/responsive.service";
import {IdentityModel} from "../identity/identity.model";
import {IdentityEvents} from "../identity/identity-events";
import {PromoEvents} from "../promo/promo-events";
import {OrderEvents} from "../order/order-events";
import {AppPromoData, AppPromoPromotedSellableData} from "../identity/member-summary-entity.model";
import {OrderItem, OrderType} from "../order/order";
import {SellableSizesData, SellableSizesSearchModel} from "./sellable-sizes-search.model";
import {
    MenuCategoryData,
    MenuCompositeData,
    MenuDietaryIndicatorsData,
    MenuExtraGroupData,
    MenuExtraItemsData,
    MenuExtraItemsMenuItemData,
    MenuExtraProductData,
    MenuPlgData,
    MenuPlgItemsData,
    MenuPlgItemsMenuItemData,
    MenuPlgSellableData,
    MenuProductExtraUsage,
    MenuProductLinePlgQtyData,
    MenuProductLineQtyData,
    MenuQtyData,
    MenuSellableCategoryLookupData,
    MenuSellableCustomObjectData,
    MenuSellableData,
    MenuSellableMetaTagDefinitionData,
    MenuItem,
    MenuItemData,
    MenuItemSellableData,
    CountdownData,
    MenuEntityModel,
    MergedScheduleData,
    OrderTypeInfoData,
    ScheduleData,
} from "./menu-entity.model";
import {ActiveSchedulesSearchModel} from "../schedule/active-schedules-search.model";
import {StoreData} from "../store/store-search.model";

const levenshtein = require("js-levenshtein");

class BuildRequest {
    public requestCount: number = 1;

    constructor(public activatedPromoChanged: boolean = false) {
    }

    update(activatedPromoChanged: boolean): BuildRequest {
        this.activatedPromoChanged = this.activatedPromoChanged || activatedPromoChanged;
        this.requestCount++;
        return this;
    }
}

export class MenuItemInCategory {
    constructor(public menuItem: MenuItemData,
                public category: MenuCategoryData) {
    }
}

export class IndexedMenuItem {
    constructor(public itemInCategory: MenuItemInCategory,
                public weighting: number) {
    }
}

export class MenuItemSize {
    sellableSize: SellableSizesData;
    menuItem: MenuItemData;

    constructor(sellableSize: SellableSizesData, menuItem: MenuItemData) {
        this.sellableSize = sellableSize;
        this.menuItem = menuItem;
    }
}

@Injectable()
export class MenuModel implements OnDestroy {

    // Avoid injection dependency on OrderModel
    private _orderModel: OrderModel;

    private subscriptions: Subscription = new Subscription();

    constructor(private sessionModel: SessionModel,
                private responsiveService: ResponsiveService,
                private orderingContextModel: OrderingContextModel,
                private activeSchedulesSearchModel: ActiveSchedulesSearchModel,
                private promoModel: PromoModel,
                private identityModel: IdentityModel,
                private menuEntityModel: MenuEntityModel,
                private sellableSizesSearchModel: SellableSizesSearchModel) {
        console.log("--- MenuModel");

        MenuEvents.menuModel.next(this);

        this.subscriptions.add(this.responsiveService.mode$.subscribe(() => {
            if (this.responsiveService.desktop) {
                this.selectDefaultCategory();
            }
        }));

        this.subscriptions.add(OrchestrationEvents.sessionReset$.subscribe(() => {
            this.menuLoaded = false;
            this.selectCategory(undefined);
            MenuEvents.currentMenu.next(undefined);
        }));

        this.subscriptions.add(this.initialiseMenuItem$
            .concatMap((menuItem: MenuItemData) => new Observable<MenuItemData>((sub: Subscriber<MenuItemData>) => {
                sub.next(menuItem);
                setTimeout(() => sub.complete());
            }))
            .subscribe(async (menuItem: MenuItemData) => {
                this.initialiseItem(menuItem);
                MenuEvents.menuItemInitialised$.next(menuItem);
            }));

        // When the store changes, we are expecting the menu to be reloaded (requested by the ordering page).
        this.subscriptions.add(OrderingContextEvents.storeDetailsChanged$.subscribe((storeChanged: boolean) => storeChanged && (this.menuLoadPending = true)));

        this.subscriptions.add(IdentityEvents.currentMember$.subscribe(() => {
            const newMemberId: string = this.identityModel.currentMember ? this.identityModel.currentMember.memberId : "-1";
            if (this.buildPerformedForMemberId && this.buildPerformedForMemberId !== newMemberId) {
                this.requestMenuBuild("member id changed");
            }
        }));
        this.subscriptions.add(PromoEvents.activatedPromo$.subscribe(() => this.requestMenuBuild("promo activated", true)));

        // Note: if the store changes, we rely upon the ordering page to initiate the menu reload,
        // as it coordinates this with the load of the app and stores.
    }

    set orderModel(orderModel: OrderModel) {
        this._orderModel = orderModel;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.buildSubscription && this.buildSubscription.unsubscribe();
    }

    /* ==================================================================
        MENU ENTITY MANAGEMENT
        The menu entity is the primary definition of the menu
       ================================================================== */

    public pageImage: FedeltaImageData = new FedeltaImageData();
    public pageImageOpacity: number = 1;

    public menuLoaded: boolean = false;
    private menuUnloaded: boolean = false;
    private menuLoadPending: boolean = false;

    private get menuId(): number {
        return this.orderingContextModel.currentStore ? this.orderingContextModel.currentStore.menuId
            : this.sessionModel.currentApp ? this.sessionModel.currentApp.defaultMenuId
                : null;
    }

    public async loadMenu(reason: string): Promise<void> {
        if (!this.menuId) {
            this.menuLoadPending = false;
            return;
        }

        console.log(Date.now(), "Loading menu:", reason);

        // Load of menu requires the availability context to be set, which requires the current schedules.
        await this.orderingContextModel.refreshActiveSchedules();

        this.menuLoaded = false;
        this.menuLoadPending = false;
        try {
            await this.sellableSizesSearchModel.retrieve();
        } catch (error) {
            console.log(error);
            throw error;
        }
        try {
            await this.menuEntityModel.loadEntity(this.menuId + "," +
                (this.orderingContextModel.expectedCompletion ? (this.orderingContextModel.expectedCompletionAsString) : "") + "," +
                (this.orderingContextModel.orderType ? (OrderType.toString(this.orderingContextModel.orderType)) : ""));
        } catch (error) {
            this.menuUnloaded = true;
            console.log(error);
            throw error;
        }
        this.menuLoaded = true;
        this.menuUnloaded = false;

        // Link up menu items to references in groups
        this.menuEntityModel.data.categories.forEach((category: MenuCategoryData) => {
            category.menuItems.forEach((menuItem: MenuItemData) => {
                if (this.isCompositeItem(menuItem)) {
                    menuItem.sellable.composite.prodLineQuantities.forEach((prodLineQty: MenuProductLineQtyData) => {
                        prodLineQty.productLine.menuItem = this.findAnyMenuItemForSellable(prodLineQty.productLine.sellableId);
                    });
                }
            });
        });
        this.menuEntityModel.data.extras.forEach((extraGroup: MenuExtraGroupData) => {
            extraGroup.extraProducts.forEach((extraProduct: MenuExtraProductData) => {
                extraProduct.menuItem = this.findAnyExtraItemsMenuItemForSellable(extraProduct.productLine.sellableId);
                if (extraProduct.menuItem) {
                    extraProduct.menuItem.sellable.integratedProductImage = extraProduct.productLine.integratedProductImage;
                }
            });
        });
        this.menuEntityModel.data.plgs.forEach((plg: MenuPlgData) => {
            plg.sellableLines.forEach((plgSellable: MenuPlgSellableData) => {
                plgSellable.menuItem = this.findAnyPlgItemsMenuItemForSellable(plgSellable.sellable.sellableId);
                if (plgSellable.menuItem) {
                    plgSellable.menuItem.sellable.integratedProductImage = plgSellable.sellable.integratedProductImage;
                    if (plgSellable.menuItem) {
                        plgSellable.sellable.menuItem = <MenuItemData>plgSellable.menuItem;
                    }
                }
            });
        });

        this._sellableQtyAvailable = {};
        if (this.menuEntityModel.data.countdowns) {
            this.menuEntityModel.data.countdowns.forEach((countdown: CountdownData) => this._sellableQtyAvailable[countdown.sellableId] = countdown.qtyAvailable);
        }

        this.showItemImages = this.responsiveService.mobile ? this.menuEntityModel.data.showMobileItemImages : this.menuEntityModel.data.showItemImages;
        this.showCategoryImages = this.responsiveService.mobile ? this.menuEntityModel.data.showMobileCategoryImages : this.menuEntityModel.data.showCategoryImages;
        this.showSubcategoryImages = this.responsiveService.mobile ? this.menuEntityModel.data.showMobileSubcategoryImages : this.menuEntityModel.data.showSubcategoryImages;
        this.defaultItemImage = this.menuEntityModel.data.defaultItemImage && this.menuEntityModel.data.defaultItemImage.id ? this.menuEntityModel.data.defaultItemImage : undefined;
        this.defaultCategoryImage = this.menuEntityModel.data.defaultCategoryImage && this.menuEntityModel.data.defaultCategoryImage.id ? this.menuEntityModel.data.defaultCategoryImage : undefined;
        this.pageImage = this.menuEntityModel.data.pageImage && this.menuEntityModel.data.pageImage.id ? this.menuEntityModel.data.pageImage : new FedeltaImageData();
        this.pageImageOpacity = this.menuEntityModel.data.pageImageOpacity;

        this.requestMenuBuild("menu loaded");
        MenuEvents.currentMenu.next(this.menuEntityModel.data);

        // Update "theme" css vars for menu page background image
        const cssVars: any = {};
        if (this.menuEntityModel.data.pageImageBackgroundColor) {
            cssVars["--woa-theme-color-menu-page-image-pair-background"] = Color.colorStringToRGB(this.menuEntityModel.data.pageImageBackgroundColor);
            cssVars["--woa-theme-color-menu-page-image-pair-background-shader"] = Color.colorStringToShaderRGB(this.menuEntityModel.data.pageImageBackgroundColor, true);
        }
        if (this.menuEntityModel.data.pageImageTextColor) {
            cssVars["--woa-theme-color-menu-page-image-pair-foreground"] = Color.colorStringToRGB(this.menuEntityModel.data.pageImageTextColor);
            cssVars["--woa-theme-color-menu-page-image-pair-foreground-shader"] = Color.colorStringToShaderRGB(this.menuEntityModel.data.pageImageTextColor);
        }
        CssVarHelper.applyVars(document.documentElement, cssVars);
    }

    public unloadMenu(): void {
        this.menuLoaded = false;
        this.menuUnloaded = true;
        this.requestMenuBuild("menu unloaded");
        MenuEvents.currentMenu.next(undefined);
    }

    public async orderItemWhenMenuLoaded(menuItemSelection: MenuItemSelection): Promise<void> {
        MenuEvents.itemOrderPending$.next(true);
        setTimeout(() => {
            if (this.menuLoaded && !this.menuLoadPending) {
                MenuEvents.itemOrderPending$.next(false);
                OrderEvents.requestOrderItem$.next(menuItemSelection);
            } else if (!this.menuUnloaded) {

                // Try again in half a second
                this.orderItemWhenMenuLoaded(menuItemSelection);
            }
        }, 500);
    }

    /* ============================================================================
        MENU BUILD
        Construction of the menu presentation model from the menu definition
       ============================================================================ */

    private buildRequested$: Subject<void> = new Subject<void>();
    private buildDebouncer: Observable<void> = this.buildRequested$.debounceTime(500);
    private buildSubscription: Subscription;
    private latestBuildRequest: BuildRequest;
    private building: boolean = false;
    private buildPerformedForMemberId: string;
    private activatedPromo: AppPromoData;

    private requestMenuBuild(reason: string, activatedPromoChanged: boolean = false): void {
        console.log(Date.now(), "Menu rebuild requested:", activatedPromoChanged, reason);

        // Record the build request. Subsequent requests during a debounce period are merged.
        this.latestBuildRequest = this.latestBuildRequest ? this.latestBuildRequest.update(activatedPromoChanged) : new BuildRequest(activatedPromoChanged);

        // Subscribe to build requests on the first request
        if (!this.buildSubscription) {
            this.buildSubscription = this.buildDebouncer.subscribe(async (): Promise<void> => {

                // Defer concurrent build attempts by re-requesting.
                // As requests are debounced, any further builds triggered by actions DURING build will effectively be batched.
                if (this.building) {
                    console.log(Date.now(), "Deferring concurrent menu build");
                    setTimeout(() => this.buildRequested$.next(), 500);
                    return;
                }

                // Perform the merged build request
                const request: BuildRequest = this.latestBuildRequest;
                this.latestBuildRequest = undefined;
                return this.buildMenu(request);
            });
        }

        // Queue the request to the debouncer
        this.buildRequested$.next();
    }

    private buildMenu(request: BuildRequest): void {
        this.building = true;
        try {
            this.categories = [];
            if (!this.menuLoaded) {
                console.log(Date.now(), "Not performing menu build for " + request.requestCount + " merged request(s) - menu is not yet loaded");
                return;
            }

            console.log(Date.now(), "Performing menu build for " + request.requestCount + " merged request(s)");
            this.buildPerformedForMemberId = this.identityModel.currentMember ? this.identityModel.currentMember.memberId : "-1";

            this.categories = [...this.menuEntityModel.data.categories];

            // Add promoted menu categories
            if (this.identityModel.currentMember) {
                this.identityModel.currentMember.promos
                    .filter((promo: AppPromoData) =>
                        promo.isAvailableForOrderType(this.orderingContextModel.orderType) &&
                        !promo.triggered &&
                        (!this.promoModel.activatedPromo || promo.id !== this.promoModel.activatedPromo.id) &&
                        promo.displayPromotedMenuCategory &&
                        this.promoModel.canPromoteSellablesAsCategory(promo))
                    .forEach((promo: AppPromoData) => this.addPromotedCategory(this.buildPromotedCategory(promo),
                        this.responsiveService.desktop && !this.promoModel.activatedPromo && !this._selectedCategory));
            }

            // Add/remove a category for the currently activated promo
            if (this.activatedPromo !== this.promoModel.activatedPromo) {
                this.activatedPromo = this.promoModel.activatedPromo;
                if (this.activatedPromo) {
                    this.activatedPromotedCategory = this.buildPromotedCategory(this.activatedPromo);
                } else {
                    this.activatedPromotedCategory = undefined;
                }
            }
            if (this.activatedPromotedCategory) {
                this.addPromotedCategory(this.activatedPromotedCategory, request.activatedPromoChanged);
                if (request.activatedPromoChanged) {
                    setTimeout(() => MenuEvents.activatedPromotedCategoryAdded$.next(this.activatedPromotedCategory));
                }
            }

            // Resolve the currently selected category after a reload of the menu
            if (!this.categories.some((category: MenuCategoryData) => {
                if (category === this._selectedCategory || category.name === this.selectedCategoryName) {
                    if (category.id) {
                        this.selectCategory(this.categoryHasItems(category) ? category : undefined, true);
                    }
                    return true;
                }
            })) {
                this.selectCategory(undefined, true);
            }

            this.superCategoryItemCounts.clear();
            this.subToSuperCategories.clear();
            this.categoryDisplayNames.clear();
            this.categoryImages.clear();
            this.categoryItems.clear();
            this.menuItemKeywordIndex.clear();

            this.menuItemSizesBuilt.clear();
            this.menuItemSizes.clear();
            this.menuItemSizeQualifiers.clear();
            this.menuItemSizeSummaries.clear();

            this.categories
                .filter((category: MenuCategoryData) => this.categoryIsAvailable(category))
                .forEach((category: MenuCategoryData) => {

                    // Standardise category name case, and adjust subcategory names to match their super-category.
                    this.categoryDisplayNames.set(category, this.categoryTitleCase(category.name));

                    // Get the filtered list of items for each category.
                    const menuItems: MenuItemData[] = category.menuItems.filter((menuItem: MenuItemData) =>
                        !menuItem.hidden &&
                        !this.isSuppressedItem(menuItem) &&
                        this.itemIsAvailable(menuItem) &&
                        !(this.menuEntityModel.data.hideOutOfStock && this.itemOutOfStock(menuItem)));
                    menuItems.sort((a, b) => Number(this.promoModel.isFeaturedSellable(b.sellable.sellableId)) - Number(this.promoModel.isFeaturedSellable(a.sellable.sellableId)));
                    this.categoryItems.set(category, menuItems);

                    // Accumulate the number of visible items in the super categories.
                    // Search for subcategories and build a map to relate them to their super categories.
                    const itemCount: number = menuItems.length;
                    const i: number = category.name.indexOf("\\");
                    if (i > 0) {
                        const superCategoryName: string = category.name.substring(0, i);
                        this.categories.forEach((superCategory) => {
                            if (superCategory.name === superCategoryName) {
                                this.subToSuperCategories.set(category, superCategory);
                                this.categoryDisplayNames.set(category, this.categoryTitleCase(category.name.substring(i + 1)));
                                if (itemCount > 0) {
                                    if (this.superCategoryItemCounts.has(superCategory)) {
                                        this.superCategoryItemCounts.set(superCategory, this.superCategoryItemCounts.get(superCategory) + itemCount);
                                    } else {
                                        this.superCategoryItemCounts.set(superCategory, itemCount);
                                    }
                                }
                            }
                        });
                    }
                });

            // Selectable categories are those without a super category and which have visible items.
            this.selectableCategories = this.categories.filter((category: MenuCategoryData) => !this.isSubcategory(category) && this.categoryHasItems(category));

            if (this.responsiveService.desktop) {
                this.selectDefaultCategory();
            }

            // Determine the image (if required) for each category:
            // Use any directly assigned images.
            // Failing that use the default image if specified.
            // Failing that, use the image of the first item in the category with an image.
            // Failing that, use the image of the first subcategory in the category with an image.
            this.categoryImages.clear();
            if (this.menuEntityModel.data.showCategoryImages || this.menuEntityModel.data.showSubcategoryImages || this.menuEntityModel.data.showMobileCategoryImages || this.menuEntityModel.data.showMobileSubcategoryImages) {
                this.categories.forEach((category: MenuCategoryData) => {
                    if (category.categoryImage && (category.categoryImage.id || category.categoryImage.image)) {
                        this.categoryImages.set(category, category.categoryImage);
                    } else if (this.defaultCategoryImage) {
                        this.categoryImages.set(category, this.defaultCategoryImage);
                    } else if (this.categoryHasItems(category, false)) {
                        this.categoryItems.get(category).some((menuItem: MenuItemData) => {
                            const itemImage: FedeltaImageData = this.getItemImage(category, menuItem, !category.id);
                            if (itemImage) {
                                this.categoryImages.set(category, itemImage);
                            }
                            return !!itemImage;
                        });
                    }
                });
                this.categories.forEach((category: MenuCategoryData) => {
                    if (!this.categoryImages.has(category) && this.isSuperCategory(category)) {
                        Array.from(this.subToSuperCategories.keys()).some((subcategory: MenuCategoryData) => {
                            if (this.subToSuperCategories.get(subcategory) === category) {
                                const image: FedeltaImageData = this.categoryImages.get(subcategory);
                                if (image) {
                                    this.categoryImages.set(category, image);
                                    return true;
                                }
                            }
                            return false;
                        });
                    }
                });
            }

            this.buildSelectedCategory();

            MenuEvents.menuBuilt$.next();
        } finally {
            this.building = false;
        }
    }

    /* ==================================================================
        MENU CATEGORY PRESENTATION
       ================================================================== */

    public selectableCategories: MenuCategoryData[];
    public defaultCategoryImage: FedeltaImageData = undefined;
    public showCategoryImages: boolean = false;
    public showSubcategoryImages: boolean = false;

    private categories: MenuCategoryData[];
    private subToSuperCategories: Map<MenuCategoryData, MenuCategoryData> = new Map<MenuCategoryData, MenuCategoryData>();
    private superCategoryItemCounts: Map<MenuCategoryData, number> = new Map();
    private categoryDisplayNames: Map<MenuCategoryData, string> = new Map();
    private categoryImages: Map<MenuCategoryData, FedeltaImageData> = new Map();
    private categoryItems: Map<MenuCategoryData, MenuItemData[]> = new Map();

    private categoryTitleCase(categoryName: string) {
        categoryName = categoryName.trim();

        // Only convert categories which are not already mixed case
        return categoryName[0] === "`" ? categoryName.slice(1)
            : categoryName !== categoryName.toUpperCase() && categoryName !== categoryName.toLowerCase() ? categoryName
                : categoryName.split(/\s+/).map((word: string, i: number) =>
                    ["IPA", "XPA", "NEIPA", "RTD"].indexOf(word) >= 0 ? word
                        : i > 0 && ["and", "or", "in", "of", "on"].indexOf(word.toLowerCase()) >= 0 ? word.toLowerCase()
                            : word[0].toUpperCase() + word.slice(1).toLowerCase()).join(" ");
    }

    private isSuperCategory(category: MenuCategoryData): boolean {
        return this.superCategoryItemCounts.has(category);
    }

    private isSubcategory(category: MenuCategoryData, superCategory?: MenuCategoryData): boolean {
        return superCategory ? this.subToSuperCategories.get(category) === superCategory : this.subToSuperCategories.has(category);
    }

    public getCategoryDisplayName(category: MenuCategoryData): string {
        return this.categoryDisplayNames.get(category) || (category ? this.categoryTitleCase(category.name) : undefined);
    }

    public getCategoryPath(category: MenuCategoryData): string {
        return (this.isSubcategory(category) ? (this.getCategoryDisplayName(this.subToSuperCategories.get(category)) + " ► ") : "") + this.getCategoryDisplayName(category);
    }

    public getCategoryImage(category: MenuCategoryData): FedeltaImageData {
        return this.categoryImages.get(category);
    }

    public get selectedCategoryWaitTime(): number {
        return (this._selectedCategory && this._selectedCategory.currentWaitTimeMinutes) || 0;
    }

    public getCategoryItems(category: MenuCategoryData): MenuItemData[] {
        return this.categoryItems.get(category);
    }

    private categoryIsAvailableForOrderType(category: MenuCategoryData, orderType: OrderType = this.orderingContextModel.orderType): boolean {
        return category && (!orderType ||
            ((!category.orderTypes || !category.orderTypes.length || category.orderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType))) &&
                (!category.exclOrderTypes || !category.exclOrderTypes.length || !category.exclOrderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType)))));
    }

    private categoryIsAvailableOnSchedules(category: MenuCategoryData): boolean {
        return category && this.anyMergedScheduleIsActive(category.mergedSchedules);
    }

    private categoryIsAvailable(category: MenuCategoryData): boolean {
        // Note - filtered by store on server
        return !category.id || (!category.hidden &&
            this.categoryIsAvailableForOrderType(category) &&
            this.categoryIsAvailableOnSchedules(category));
    }

    private categoryHasItems(category: MenuCategoryData, includeSubCategories: boolean = true): boolean {
        return !this.categoryIsAvailable(category) ? false
            : this.categoryItems.has(category) && this.categoryItems.get(category).length ? true
                : includeSubCategories && this.isSuperCategory(category) ? this.superCategoryItemCounts.get(category) > 0
                    : false;
    }

    /* ==================================================================
        PROMOTED CATEGORIES
       ================================================================== */

    private activatedPromotedCategory: MenuCategoryData;

    private buildPromotedCategory(promo: AppPromoData): MenuCategoryData {
        const category: MenuCategoryData = new MenuCategoryData();
        category.menuItems = promo.promotedSellables
            .map((promoted: AppPromoPromotedSellableData) => this.findMergedItemForSellable(
                promoted.sellable.sellableId, this.orderingContextModel.orderType,
                undefined, undefined, true, promo.suppressCategoryProducts && !promoted.promoteIfOrdered))
            .filter((menuItem: MenuItemData) => menuItem);
        if (!category.menuItems.length) {
            return undefined;
        }
        if (category) {
            category.id = promo === this.promoModel.activatedPromo ? undefined : -1;
            category.name = promo.categoryTitle || promo.appText1 || promo.name;
            category.description = promo.showDescriptionOnCategory ? (promo.categoryDescription || promo.appText2) : undefined;
            category.categoryImage = !promo.showImageOnCategory ? undefined
                : promo.categoryImage && promo.categoryImage.id ? promo.categoryImage
                    : promo.image && promo.image.id ? promo.image
                        : this.defaultPromoImage;
            category.mergedSchedules = [];
            category.hasHideWhenOrderedItems = category.menuItems.some((menuItem: MenuItemData) => menuItem.hideWhenOrdered);
            category.itemFeatureTag = promo.menuItemFeatureTag;
            category.featured = true;
        }
        return category;
    }

    private get defaultPromoImage(): FedeltaImageData {
        if (!this.sessionModel.currentApp.defaultImage) {
            return undefined;
        }
        const image: FedeltaImageData = EntityData.create(FedeltaImageData);
        image.id = 0;
        image.image = "data:image;base64," + this.sessionModel.currentApp.defaultImage;
        return image;
    }

    private addPromotedCategory(category: MenuCategoryData, select: boolean): void {
        this.categories.unshift(category);
        if (select) {
            this.selectCategory(category);
        }
    }

    public deactivatePromotedCategory(): void {
        if (this.activatedPromotedCategory) {
            this.promoModel.activatedPromo = undefined;
            this.requestMenuBuild("promo deactivated");
        }
    }

    /* ==================================================================
        MENU ITEM PRESENTATION
       ================================================================== */

    public showItemImages: boolean = false;
    public defaultItemImage: FedeltaImageData = undefined;

    private initialiseMenuItem$: Subject<MenuItemData> = new Subject();
    private _sellableQtyAvailable: { [sellableId: number]: number } = {};

    private findAnyMenuItemForSellable(sellableId: number): MenuItemData {
        let menuItem: MenuItemData = null;
        this.menuEntityModel.data.categories.some((category: MenuCategoryData) => {
            menuItem = category.menuItems.find((item: MenuItemData) => item.sellable.sellableId === sellableId);
            return menuItem != undefined;
        });
        return menuItem;
    }

    public isItemInitialised(menuItem: MenuItemData): boolean {
        if (menuItem.initialised && menuItem.extrasInitialised) {
            return true;
        }
        if (menuItem.initialisationPending) {
            return false;
        }
        menuItem.initialisationPending = true;
        this.initialiseMenuItem$.next(menuItem);
        return false;
    }

    public initialiseItem(menuItem: MenuItemData, initialiseExtras: boolean = true): void {
        if (!menuItem.initialised || (initialiseExtras && !menuItem.extrasInitialised)) {
            if (this.isCompositeItem(menuItem)) {
                this.getCompositeLines(menuItem.sellable.composite).forEach((compositeLine: MenuQtyData) => {
                    if (this.isProductLineQty(compositeLine)) {
                        const productLineQty: MenuProductLineQtyData = <MenuProductLineQtyData>compositeLine;
                        if (!menuItem.initialised) {
                            productLineQty.productLine.menuItem =
                                (<MenuItemData>this.findAnyPlgItemsMenuItemForSellable(productLineQty.productLine.sellableId)) ||
                                this.findAnyMenuItemForSellable(productLineQty.productLine.sellableId);
                        }
                        if (initialiseExtras) {
                            this.initialiseSellableExtras(productLineQty.productLine);
                        }
                    } else if (this.isProductLinePlgQty(compositeLine)) {
                        const productLineQty: MenuProductLinePlgQtyData = <MenuProductLinePlgQtyData>compositeLine;
                        if (!menuItem.initialised) {
                            productLineQty.plg = this.menuEntityModel.data.plgs.find((plg: MenuPlgData) => plg.plgId === productLineQty.plg.plgId);
                        }
                        productLineQty.plg.sellableLines.forEach((sellableLine: MenuPlgSellableData) => {
                            if (initialiseExtras) {
                                this.initialiseSellableExtras(sellableLine.sellable);
                            }
                        });
                    }
                });
            } else if (initialiseExtras) {
                this.initialiseSellableExtras(menuItem.sellable);
            }

            // Calculate the default prices including extras
            const defaultExtrasPrice: number = menuItem.sellable.extrasInitialised ? this.getDefaultExtrasPriceForSellable(menuItem.sellable) : 0;
            menuItem.sellable.defaultPrice = (menuItem.sellable.price + defaultExtrasPrice) * (menuItem.sellable.minWebOrderQty || 1);
            menuItem.sellable.defaultGuestPrice = (menuItem.sellable.guestPrice + defaultExtrasPrice) * (menuItem.sellable.minWebOrderQty || 1);

            menuItem.initialised = true;
            menuItem.extrasInitialised = initialiseExtras;
            menuItem.initialisationPending = false;
        }
    }

    public menuContainsItem(menuItem: MenuItemData): boolean {
        return this.menuEntityModel.data.categories.some((category: MenuCategoryData) =>
            category.menuItems.some((existingMenuItem: MenuItemData) => existingMenuItem.id === menuItem.id));
    }

    public findMergedItemForSellable(sellableId: number, orderType: OrderType, sizeGroup?: number, selectedMenuItem?: MenuItemData, allowOutOfStock: boolean = false, hideWhenOrdered: boolean = false, initialiseExtras: boolean = true): MenuItemData {
        let menuItems: MenuItemData[] = selectedMenuItem ? [selectedMenuItem] : [];
        this.menuEntityModel.data.categories.forEach((category: MenuCategoryData) =>
            menuItems = menuItems.concat(category.menuItems.filter((menuItem: MenuItemData) =>
                (selectedMenuItem === undefined || menuItem.id !== selectedMenuItem.id) &&
                menuItem.sellable.sellableId === sellableId &&
                (!this.sellableOutOfStock(sellableId) || allowOutOfStock) &&
                this.itemIsAvailableForOrderType(menuItem, orderType))));
        if (sizeGroup != undefined && !menuItems.some((menuItem: MenuItemData) => menuItem.sellableSizeGroup === sizeGroup)) {
            return undefined;
        }
        if (menuItems.length == 0) {
            return undefined;
        }
        this.initialiseItem(menuItems[0], initialiseExtras);

        const merged: MenuItemData = new MenuItemData();
        merged.id = menuItems[0].id;
        merged.hidden = false;
        merged.hideWhenOrdered = hideWhenOrdered;
        merged.orderTypes = [];
        merged.exclOrderTypes = [];
        const combineSchedules: boolean = menuItems.length > 1 && menuItems.some((menuItem: MenuItemData) => this.itemIsScheduled(menuItem));
        if (combineSchedules) {
            merged.mergedSchedules = [];
        } else {
            merged.mergedSchedules = menuItems[0].mergedSchedules;
        }
        merged.sellable = menuItems[0].sellable;
        merged.stores = [...menuItems[0].stores];
        merged.exclStores = [...menuItems[0].exclStores];
        merged.membershipTypeIds = [...menuItems[0].membershipTypeIds];
        merged.includeGuests = menuItems[0].includeGuests;
        merged.exclMembershipTypeIds = [...menuItems[0].exclMembershipTypeIds];
        merged.excludeGuests = menuItems[0].excludeGuests;
        merged.promotedFeatureTag = menuItems[0].promotedFeatureTag;
        merged.includeOptionOnUntaggedItems = menuItems[0].includeOptionOnUntaggedItems;
        merged.tagItemOnOptionSelection = menuItems[0].tagItemOnOptionSelection;

        menuItems.forEach((menuItem: MenuItemData) => {
            merged.name = merged.name || menuItem.name;
            merged.orderTypes = [...new Set([...merged.orderTypes, ...menuItem.orderTypes])];
            merged.exclOrderTypes = [...new Set([...merged.exclOrderTypes, ...menuItem.exclOrderTypes])];
            merged.filterExtras = merged.filterExtras || menuItem.filterExtras;
            merged.filterPlgItems = merged.filterPlgItems || menuItem.filterPlgItems;
            merged.filterTags = merged.filterTags || menuItem.filterTags;
            merged.sellableSizeGroup = merged.sellableSizeGroup || menuItem.sellableSizeGroup;
            merged.courseId = merged.courseId || menuItem.courseId;
            if (menuItem.productImage && menuItem.productImage.id && !(merged.productImage && merged.productImage.id)) {
                merged.productImage = menuItem.productImage;
            }
            if (combineSchedules) {
                merged.mergedSchedules = merged.mergedSchedules.concat(menuItem.mergedSchedules);
            }
        });
        if (sizeGroup != undefined) {
            merged.sellableSizeGroup = sizeGroup;
        }
        return merged;
    }

    private isSuppressedItem(menuItem: MenuItemData): boolean {
        return menuItem.hideWhenOrdered && this._orderModel.hasOrderedSellablePersonally(menuItem.sellable.sellableId);
    }

    public isSimpleProductItem(menuItem: MenuItemData, ignoreSizes: boolean = false): boolean {
        const sellableSizes: SellableSizesData[] = ignoreSizes ? undefined : this.sellableSizesSearchModel.data;
        this.initialiseItem(menuItem);
        if (menuItem.sellableSizeGroup && sellableSizes) {
            const sizes: MenuItemSize[] = this.findSizesForItem(sellableSizes, menuItem);
            if (sizes.length) {
                return !sizes.some((size) => this.isCompositeItem(size.menuItem) || this.itemHasVisibleExtras(size.menuItem));
            }
        }
        return !this.isCompositeItem(menuItem) && !this.itemHasVisibleExtras(menuItem);
    }

    private itemHasVisibleExtras(menuItem: MenuItemData): boolean {
        this.initialiseItem(menuItem);
        return menuItem.sellable.extras.length && menuItem.sellable.extras.some((extraGroup: MenuExtraGroupData) =>
            extraGroup.extraProducts.length && extraGroup.extraProducts.some((extraProduct: MenuExtraProductData) => {
                return (!extraProduct.menuItem && !menuItem.filterExtras) ||
                    (extraProduct.menuItem && !extraProduct.menuItem.hidden && this.extraItemIsAvailableForFilters(extraProduct.menuItem, menuItem));
            }));
    }

    public itemSummaryContainsDetails(menuItem: MenuItemData, ignoreSizes: boolean = false): boolean {
        return (!!menuItem.sellableSizeGroup && this.isSimpleProductItem(menuItem, ignoreSizes)) ||
            !!this.getItemDescription(menuItem) ||
            menuItem.sellable.dietaryIndicators.length > 0 ||
            !!this.sessionModel.currentApp.dietaryStatement;
    }

    /**
     * Items are named using their size relation group external name if defined, or otherwise the item display name.
     */
    public getItemName(menuItem: MenuItemData): string {
        if (!menuItem) {
            return undefined;
        }
        if (menuItem.sellableSizeGroup && this.menuEntityModel.data.useSizeGroupNames) {
            const size: SellableSizesData = this.getItemSellableSize(this.sellableSizesSearchModel.data, menuItem);
            if (size && size.externalName) {
                return size.externalName;
            }
        }
        return menuItem.name;
    }

    private isCompositeItem(menuItem: MenuItemData): boolean {
        return this.isCompositeSellable(menuItem.sellable);
    }

    public isNormalMenuItem(menuItem: MenuItem): boolean {
        return menuItem && menuItem.sellable["name"] != undefined;
    }

    private isCompositeSellable(sellable: MenuItemSellableData): boolean {
        return sellable.composite && !!sellable.composite.sellableId;
    }

    public getCompositeLines(composite: MenuCompositeData): MenuQtyData[] {
        const items: Array<MenuQtyData> = [...composite.prodLineQuantities, ...composite.prodLinePlgQuantities];
        items.sort((a: MenuQtyData, b: MenuQtyData) => a.indexOrder - b.indexOrder);
        return items;
    }

    public getSellableExternalDescription(sellable: MenuSellableData): string {
        return sellable.externalDescription || sellable.integratedExternalDescription;
    }

    public getItemImage(category: MenuCategoryData, menuItem: MenuItem, forPromo?: boolean, useDefault: boolean = false): FedeltaImageData {
        return !menuItem ? undefined
            : this.promoModel.getPromotionalImage(category, menuItem.sellable.sellableId, forPromo) || this.getStandardItemImage(menuItem, useDefault);
    }

    public getStandardItemImage(menuItem: MenuItem, useDefault: boolean = false): FedeltaImageData {
        return !menuItem ? undefined
            : menuItem.productImage && menuItem.productImage.id ? menuItem.productImage
                : menuItem.sellable.integratedProductImage && menuItem.sellable.integratedProductImage.id ? menuItem.sellable.integratedProductImage
                    : useDefault ? this.defaultItemImage
                        : undefined;
    }

    public getItemDescription(menuItem: MenuItem): string {
        return !menuItem || !menuItem.sellable ? undefined : this.getSellableExternalDescription(<MenuSellableData>menuItem.sellable);
    }

    public itemOutOfStock(menuItem: MenuItem): boolean {
        if (menuItem && this.isNormalMenuItem(menuItem)) {
            this.initialiseItem(<MenuItemData>menuItem);
        }
        return menuItem && menuItem.sellable && (this.sellableOutOfStock(menuItem.sellable.sellableId) ||
            (this.isNormalMenuItem(menuItem) && (<MenuItemData>menuItem).sellable.composite &&
                (<MenuItemData>menuItem).sellable.composite.prodLineQuantities.some((prodLineQty: MenuProductLineQtyData) => this.itemOutOfStock(prodLineQty.productLine.menuItem))));
    }

    public itemIsAvailableOnSchedules(menuItem: MenuItemData): boolean {
        return menuItem && this.anyMergedScheduleIsActive(menuItem.mergedSchedules);
    }

    public itemIsAvailableForOrderType(menuItem: MenuItemData, orderType: OrderType = this.orderingContextModel.orderType): boolean {
        return menuItem && (!orderType ||
            ((!menuItem.orderTypes || !menuItem.orderTypes.length || menuItem.orderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType))) &&
                (!menuItem.exclOrderTypes || !menuItem.exclOrderTypes.length || !menuItem.exclOrderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType)))));
    }

    public itemIsAvailable(menuItem: MenuItemData): boolean {
        // Note - filtered by store on server
        return this.itemIsAvailableForOrderType(menuItem) &&
            this.itemIsAvailableOnSchedules(menuItem);
    }

    public sellableQtyAvailable(sellableId: number): number {
        return this.orderingContextModel.isStoreOpenAtCompletionTime ? this._sellableQtyAvailable[sellableId] : undefined;
    }

    public sellableOutOfStock(sellableId: number): boolean {
        const qtyAvailable: number = this.sellableQtyAvailable(sellableId);
        const menuItem: MenuItemData = this.findAnyMenuItemForSellable(sellableId);
        return qtyAvailable != undefined && qtyAvailable < (menuItem ? (menuItem.sellable.minWebOrderQty || 1) : 1);
    }

    public minimumAgeAppliesToSellable(sellableId: number): boolean {
        return this.menuEntityModel.data.categories.some((category: MenuCategoryData) =>
            category.menuItems.some((menuItem: MenuItemData) => menuItem.sellable.sellableId === sellableId && menuItem.sellable.applyMinimumAge));
    }

    private filterTagsMatch(optionTags: string, productTags: string, includeOptionOnUntaggedItems: boolean) {
        const productTagsArr: string[] = productTags ? productTags.split(/\s+/).filter((tag: string) => tag) : [];
        const optionTagsArr: string[] = optionTags ? optionTags.split(/\s+/).filter((tag: string) => tag) : [];

        // If includeOptionOnUntaggedItems is true, both sides must specify filter tags for an option to be filtered out. i.e. no tags specified on either side matches everything.
        // If includeOptionOnUntaggedItems is false, no tags specified on the option still matches everything, but no tags specified on the product does not match a tagged option.
        return !optionTagsArr.length ||
            (includeOptionOnUntaggedItems && !productTagsArr.length) ||
            optionTagsArr.some((optionTag: string) => optionTag.split(/\+/).every((andTag: string) => !andTag || productTagsArr.indexOf(andTag) >= 0));
    }

    /* ==================================================================
        MENU ITEM SIZES
       ================================================================== */

    private menuItemSizesBuilt: Set<MenuItemData> = new Set();
    private menuItemSizes: Map<MenuItemData, MenuItemSize[]> = new Map();
    private menuItemSizeQualifiers: Map<MenuItemData, string> = new Map();
    private menuItemSizeSummaries: Map<MenuItemData, string[]> = new Map();
    private menuItemKeywordIndex: Map<string, IndexedMenuItem[]> = new Map();

    private findAnyExtraItemsMenuItemForSellable(sellableId: number): MenuExtraItemsMenuItemData {
        let menuItem: MenuExtraItemsMenuItemData = null;
        this.menuEntityModel.data.extraItems.some((extraItems: MenuExtraItemsData) => {
            menuItem = extraItems.menuItems.find((item: MenuExtraItemsMenuItemData) => item.sellable.sellableId === sellableId);
            return menuItem != undefined;
        });
        return menuItem;
    }

    /**
     * Determine the size options for the specified item.
     * A size label is only required if a size relation group external name is present (to qualify the size to which the price applies).
     * @param menuItem
     * @private
     */
    private buildItemSizes(menuItem: MenuItemData): void {
        if (this.menuItemSizesBuilt.has(menuItem)) {
            return;
        }

        if (menuItem.sellableSizeGroup && this.menuEntityModel.data.useSizeGroupNames) {
            const size: SellableSizesData = this.getItemSellableSize(this.sellableSizesSearchModel.data, menuItem);
            if (size && size.externalName) {
                this.menuItemSizeQualifiers.set(menuItem, size.sizeLabel);
                const sizes: MenuItemSize[] = this.getItemSizes(menuItem);
                if (sizes.length >= 2) {
                    this.menuItemSizeSummaries.set(menuItem, sizes.map((size: MenuItemSize) => this.getSizeAbbreviation(size.sellableSize)));
                }
            }
        }

        this.menuItemSizesBuilt.add(menuItem);
    }

    private getItemSellableSize(sellableSizes: SellableSizesData[], menuItem: MenuItemData): SellableSizesData {
        if (!menuItem.sellableSizeGroup) {
            return undefined;
        }
        if (menuItem.sellableSize === undefined) {
            const sellableSize: SellableSizesData = sellableSizes.find((size: SellableSizesData) => size.sellableSizeGroupId === menuItem.sellableSizeGroup && size.sellableId === menuItem.sellable.sellableId);
            menuItem.sellableSize = sellableSize ? sellableSize : null;
        }
        return menuItem.sellableSize;
    }

    /**
     * Returns the available sizes for the specified menu item.
     * Note that this method can be called using a merged item from the order edit page,
     * so its cache map is maintained independently of the qualifier and summary maps.
     * @param menuItem
     */
    public getItemSizes(menuItem: MenuItemData): MenuItemSize[] {
        if (!this.menuItemSizes.has(menuItem)) {
            this.menuItemSizes.set(menuItem, this.findSizesForItem(this.sellableSizesSearchModel.data, menuItem));
        }
        return this.menuItemSizes.get(menuItem);
    }

    public itemHasCompositeProductInSizes(menuItem: MenuItemData): boolean {
        return this.findSizesForItem(this.sellableSizesSearchModel.data, menuItem).some((menuItemSize: MenuItemSize) => this.isCompositeItem(menuItemSize.menuItem));
    }

    private findSizesForItem(sellableSizes: SellableSizesData[], menuItem: MenuItemData): MenuItemSize[] {
        const menuItemSizes: MenuItemSize[] = [];

        // Find the sellable sizes for a given sellable id within the specified size group.
        // Unfortunately, the current model is wrong in that menu items are associated with a size group rather than a size relation group.
        // This means that in theory, a given sellable could be in more than one size relation group for the specified size group.
        // We pick the first size relation group we encounter for the sellable in the specified size group, and use
        // sizes only from that size relation group (groupId).
        const sizesForSizeGroup: SellableSizesData[] = sellableSizes.filter((size: SellableSizesData) => size.sellableSizeGroupId === menuItem.sellableSizeGroup);
        const sizeForSellable: SellableSizesData = sizesForSizeGroup.find((size: SellableSizesData) => size.sellableId === menuItem.sellable.sellableId);
        const sizes: SellableSizesData[] = sizeForSellable ? sizesForSizeGroup.filter((size: SellableSizesData) => size.groupId === sizeForSellable.groupId) : [];

        if (sizes) {

            // We want to find the menu items for each of the sellable sizes in the chosen size relation group.
            // Unfortunately, the current model is wrong in that menu items are associated with a size group rather than a size relation group.
            // The best we can do, then, is find the menu items for the sellable in the same size group, which would not be the expected behaviour
            // had someone gone to the trouble of placing a sellable into multiple size relation groups for the same size group.
            // The risk here is that we choose a menu item with schedule restrictions that make it unavailable when checked during order placement,
            // even though other menu items for the same sellable and in the same size group are available.
            // In fact this could still happen even if the menu items were properly associated with size relation groups.
            // To mitigate this issue, we find all menu items for the sellable and return a merged menu item reflecting the combined details,
            // so long as at least one menu item is in the required size group.
            sizes.forEach((size: SellableSizesData) => {
                const groupMenuItem: MenuItemData = this.findMergedItemForSellable(size.sellableId, this.orderingContextModel.orderType, size.sellableSizeGroupId, undefined, true, false, false);
                if (groupMenuItem) {
                    menuItemSizes.push(new MenuItemSize(size, groupMenuItem));
                }
            });
        }
        return menuItemSizes;
    }

    public getItemSizeQualifier(menuItem: MenuItemData): string {
        if (!this.menuItemSizesBuilt.has(menuItem)) {
            this.buildItemSizes(menuItem);
        }
        return this.menuItemSizeQualifiers.get(menuItem);
    }

    public getItemSizeSummary(menuItem: MenuItemData): string[] {
        if (!this.menuItemSizesBuilt.has(menuItem)) {
            this.buildItemSizes(menuItem);
        }
        return this.menuItemSizeQualifiers.has(menuItem) && this.getItemSizes(menuItem).length > 1 ? this.menuItemSizeSummaries.get(menuItem) : undefined;
    }

    private getSizeAbbreviation(size: SellableSizesData): string {
        if (size.sizeAbbreviation) {
            return size.sizeAbbreviation;
        }
        switch (size.sizeLabel) {
            case "Schooner":
                return "Sch";
            case "Pint":
                return "Pnt";
            case "Imperial Pint":
                return "Imp";
            case "Glass":
                return "Gls";
            case "Bottle":
                return "Btl";
            case "Small":
                return "S";
            case "Regular":
                return "R";
            case "Medium":
                return "M";
            case "Large":
                return "L";
            case "Short":
                return "S";
            case "Tall":
                return "T";
            case "Grande":
                return "G";
            case "Venti":
                return "V";
            default:
                return size.sizeLabel;
        }
    }

    /**
     * Find the sellable size id for a sellable
     * @param {number} sellableSizeGroupId
     * @param {number} sellableId
     * @returns {number}
     */
    public findSellableSizeId(sellableSizeGroupId: number, sellableId: number): number {
        const size: SellableSizesData = this.sellableSizesSearchModel.data
            .filter((size: SellableSizesData) => size.sellableSizeGroupId === sellableSizeGroupId)
            .find((size: SellableSizesData) => size.sellableId === sellableId);
        return size ? size.sellableSizeId : undefined;
    }

    /* ==================================================================
        MENU ITEM EXTRAS
       ================================================================== */

    private initialiseSellableExtras(sellable: MenuSellableData): void {
        if (sellable.extrasInitialised) {
            return;
        }
        sellable.extras = sellable.extras.map((extraGroupRef: MenuExtraGroupData) => this.menuEntityModel.data.extras.find((extraGroup: MenuExtraGroupData) => extraGroup.id === extraGroupRef.id) || extraGroupRef);
        sellable.extrasInitialised = true;
    }

    /**
     * Find all sizes for the specified sellable that are in options in the specified extra group
     * @param {number} sellableId
     * @param extraGroup
     * @returns {SellableSizesData[]}
     */
    public findExtraSizes(sellableId: number, extraGroup: MenuExtraGroupData): SellableSizesData[] {
        const sizes: SellableSizesData[] = this.sellableSizesSearchModel.data.filter((size) => size.sellableId === sellableId &&
            extraGroup.extraProducts.find((value) => value.productLine.sellableId === sellableId) != undefined);
        return sizes.length ? this.sellableSizesSearchModel.data.filter((size) => size.groupId === sizes[0].groupId &&
            extraGroup.extraProducts.find((value) => value.productLine.sellableId === size.sellableId) != undefined) : undefined;
    }

    public getExtraLimit(extraProduct: MenuExtraProductData): number {

        // Limit of zero means explicitly unlimited
        if (extraProduct.qtyLimit === 0) {
            return 0;
        }

        let limit: number = extraProduct.qtyLimit || this.menuEntityModel.data.defaultExtrasLimit || 1;
        if (extraProduct.defaultOrderQty) {
            limit = Math.max(limit, extraProduct.defaultOrderQty);
        }
        return limit;
    }

    public getExtraPrice(orderItem: OrderItem, extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): number {
        if (OrderModel.SHOW_RELATIVE_PRICING_FOR_EXTRAS) {
            const price: number = extraProduct.price - orderItem.getBasePriceForGroup(extraGroup.id);
            return price === 0 ? undefined : price;
        }
        return extraProduct.price <= 0 || this.sessionModel.currentApp.showTotalIncludedExtras ? extraProduct.price
            : Math.max(0, extraProduct.price - (extraGroup.totalIncluded || 0));
    }

    public getExtraGroupEffectiveQtyLimit(extraGroup: MenuExtraGroupData): number {
        return extraGroup.groupQtyLimit == undefined ? undefined : Math.max(extraGroup.groupQtyLimit, extraGroup.extraProducts.reduce((total: number, extraProduct: MenuExtraProductData) => total + (extraProduct.defaultOrderQty || 0), 0));
    }

    public extraGroupIsOptional(extraGroup: MenuExtraGroupData): boolean {
        return !extraGroup.groupQtyMin;
    }

    public extraGroupIsSingleSelect(extraGroup: MenuExtraGroupData): boolean {
        return extraGroup.groupQtyMin === 1 && this.getExtraGroupEffectiveQtyLimit(extraGroup) === 1;
    }

    public extraGroupIsVisible(extraGroup: MenuExtraGroupData): boolean {
        return this.getExtraGroupEffectiveQtyLimit(extraGroup) !== 0 &&
            !(this.extraGroupIsSingleSelect(extraGroup) && extraGroup.extraProducts.length == 1 && extraGroup.extraProducts[0].defaultOrderQty >= 1);
    }

    public getParentExtraGroupId(extraGroup: MenuExtraGroupData, sellable: MenuSellableData): number {
        const usage: MenuProductExtraUsage = extraGroup.usages.find((usage: MenuProductExtraUsage) => usage.productLineId === sellable.sellableId);
        return usage ? usage.parentExtraId : undefined;
    }

    public extraGroupHasSingleOption(extraGroup: MenuExtraGroupData): boolean {
        return extraGroup.extraProducts.length === 1;
    }

    public extraGroupHasDefaultOptions(extraGroup: MenuExtraGroupData): boolean {
        return !!extraGroup.extraProducts.find((extraProduct: MenuExtraProductData) => {
            if (extraProduct.defaultOrderQty > 0) {
                return true;
            }
            const name: string = (extraProduct.menuItem && extraProduct.menuItem.name ? extraProduct.menuItem.name : extraProduct.productLine.name).toLowerCase();
            return name.startsWith("no ") || name.startsWith("remove ") || name.startsWith("without ") || name.startsWith("w/out ");
        });
    }

    public extraGroupHasNoChoiceWithDefaultAtLimit(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): boolean {
        return this.extraGroupHasNoChoice(extraGroup) && extraProduct.defaultOrderQty == extraGroup.groupQtyMin;
    }

    public extraGroupHasNoChoice(extraGroup: MenuExtraGroupData): boolean {
        return this.extraGroupHasSingleOption(extraGroup) && (extraGroup.groupQtyMin === this.getSingleOptionExtraLimit(extraGroup));
    }

    public getSingleOptionExtraLimit(extraGroup: MenuExtraGroupData): number {
        if (!this.extraGroupHasSingleOption(extraGroup)) {
            return undefined;
        }

        // If there is only one option, the group qty limit also applies to that option.
        const extraLimit: number = this.getExtraLimit(extraGroup.extraProducts[0]);
        const groupQtyLimit: number = this.getExtraGroupEffectiveQtyLimit(extraGroup);
        return extraLimit && groupQtyLimit ? Math.min(extraLimit, groupQtyLimit) : (extraLimit || groupQtyLimit);
    }

    public extraItemIsAvailableOnSchedules(extraItemMenuItem: MenuExtraItemsMenuItemData): boolean {
        return !extraItemMenuItem || this.anyMergedScheduleIsActive(extraItemMenuItem.mergedSchedules);
    }

    public extraItemIsAvailableForStore(extraItemMenuItem: MenuExtraItemsMenuItemData, store: StoreData = this.orderingContextModel.currentStore): boolean {
        return !extraItemMenuItem || !store ||
            ((!extraItemMenuItem.stores || !extraItemMenuItem.stores.length || extraItemMenuItem.stores.some((storeId: number) => storeId === store.storeId)) &&
                (!extraItemMenuItem.exclStores || !extraItemMenuItem.exclStores.length || !extraItemMenuItem.exclStores.some((storeId: number) => storeId === store.storeId)));
    }

    public extraItemIsAvailableForMember(extraItemMenuItem: MenuExtraItemsMenuItemData, membershipTypeId: number = this.identityModel.currentMembershipTypeId): boolean {
        return !extraItemMenuItem ||
            ((!extraItemMenuItem.membershipTypeIds || !extraItemMenuItem.membershipTypeIds.length || extraItemMenuItem.membershipTypeIds.indexOf(membershipTypeId || -1) >= 0) &&
                (!extraItemMenuItem.exclMembershipTypeIds || !extraItemMenuItem.exclMembershipTypeIds.length || extraItemMenuItem.exclMembershipTypeIds.indexOf(membershipTypeId || -1) < 0));
    }

    public extraItemIsAvailableForOrderType(extraItemMenuItem: MenuExtraItemsMenuItemData, orderType: OrderType = this.orderingContextModel.orderType): boolean {
        return !extraItemMenuItem || !orderType ||
            ((!extraItemMenuItem.orderTypes || !extraItemMenuItem.orderTypes.length || extraItemMenuItem.orderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType))) &&
                (!extraItemMenuItem.exclOrderTypes || !extraItemMenuItem.exclOrderTypes.length || !extraItemMenuItem.exclOrderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType))));
    }

    public extraItemIsAvailableForFilters(extraItemMenuItem: MenuExtraItemsMenuItemData, productMenuItem: MenuItemData, selectedFilterTags?: string): boolean {
        return !productMenuItem.filterExtras || (extraItemMenuItem && this.filterTagsMatch(extraItemMenuItem.filterTags, (productMenuItem.filterTags || "") + (selectedFilterTags || ""), extraItemMenuItem.includeOptionOnUntaggedItems));
    }

    public extraItemIsAvailable(extraItemMenuItem: MenuExtraItemsMenuItemData, productMenuItem: MenuItemData, selectedFilterTags?: string): boolean {
        return !(extraItemMenuItem && extraItemMenuItem.hidden) &&
            this.extraItemIsAvailableForStore(extraItemMenuItem) &&
            this.extraItemIsAvailableForMember(extraItemMenuItem) &&
            this.extraItemIsAvailableForOrderType(extraItemMenuItem) &&
            this.extraItemIsAvailableOnSchedules(extraItemMenuItem) &&
            this.extraItemIsAvailableForFilters(extraItemMenuItem, productMenuItem, selectedFilterTags);
    }

    public getDefaultExtrasPriceForSellable(sellable: MenuItemSellableData): number {
        return this.isCompositeSellable(sellable) ? this.getDefaultExtrasPriceForComposite(sellable.composite) : this.getDefaultExtrasPriceForProductLine(sellable);
    }

    public getDefaultExtrasPriceForProductLine(sellable: MenuSellableData): number {
        return sellable.extras.reduce((total: number, extraGroup: MenuExtraGroupData) => total + this.getDefaultPriceForExtraGroup(extraGroup), 0);
    }

    public getDefaultExtrasPriceForComposite(composite: MenuCompositeData): number {
        return composite.prodLineQuantities.reduce((total: number, prodLineQty: MenuProductLineQtyData) => total + this.getDefaultExtrasPriceForSellable(<MenuItemSellableData>prodLineQty.productLine), 0) +
            composite.prodLinePlgQuantities.reduce((total: number, prodLinePlgQty: MenuProductLinePlgQtyData) => total + this.getDefaultExtrasPriceForPlg(prodLinePlgQty.plg), 0);
    }

    public getDefaultExtrasPriceForPlg(plg: MenuPlgData): number {
        return plg.sellableLines
            .filter((plgSellable: MenuPlgSellableData) => plgSellable.defaultSellable)
            .reduce((total: number, plgSellable: MenuPlgSellableData) => total + (plgSellable.additionalPrice || 0) + this.getDefaultExtrasPriceForProductLine(plgSellable.sellable), 0);
    }

    public getDefaultPriceForExtraGroup(extraGroup: MenuExtraGroupData): number {
        return Math.max(0, extraGroup.extraProducts
            .filter((extraProduct: MenuExtraProductData) => extraProduct.defaultOrderQty)
            .reduce((total: number, extraProduct: MenuExtraProductData) => total + (extraProduct.price * extraProduct.defaultOrderQty), 0) - (extraGroup.totalIncluded || 0));
    }

    /* ==================================================================
        COMPOSITE OPTION GROUPS (PLGs)
       ================================================================== */

    private findAnyPlgItemsMenuItemForSellable(sellableId: number): MenuPlgItemsMenuItemData {
        let menuItem: MenuPlgItemsMenuItemData = null;
        this.menuEntityModel.data.plgItems.some((plgItems: MenuPlgItemsData) => {
            menuItem = plgItems.menuItems.find((item: MenuPlgItemsMenuItemData) => item.sellable.sellableId === sellableId);
            return menuItem != undefined;
        });
        return menuItem;
    }

    public plgItemIsAvailableOnSchedules(plgItemMenuItem: MenuPlgItemsMenuItemData): boolean {
        return !plgItemMenuItem || this.anyMergedScheduleIsActive(plgItemMenuItem.mergedSchedules);
    }

    public plgItemIsAvailableForStore(plgItemMenuItem: MenuPlgItemsMenuItemData, store: StoreData = this.orderingContextModel.currentStore): boolean {
        return !plgItemMenuItem || !store ||
            ((!plgItemMenuItem.stores || !plgItemMenuItem.stores.length || plgItemMenuItem.stores.some((storeId: number) => storeId === store.storeId)) &&
                (!plgItemMenuItem.exclStores || !plgItemMenuItem.exclStores.length || !plgItemMenuItem.exclStores.some((storeId: number) => storeId === store.storeId)));
    }

    public plgItemIsAvailableForMember(plgItemMenuItem: MenuPlgItemsMenuItemData, membershipTypeId: number = this.identityModel.currentMembershipTypeId): boolean {
        return !plgItemMenuItem ||
            ((!plgItemMenuItem.membershipTypeIds || !plgItemMenuItem.membershipTypeIds.length || plgItemMenuItem.membershipTypeIds.indexOf(membershipTypeId || -1) >= 0) &&
                (!plgItemMenuItem.exclMembershipTypeIds || !plgItemMenuItem.exclMembershipTypeIds.length || plgItemMenuItem.exclMembershipTypeIds.indexOf(membershipTypeId || -1) < 0));
    }

    public plgItemIsAvailableForOrderType(plgItemMenuItem: MenuPlgItemsMenuItemData, orderType: OrderType = this.orderingContextModel.orderType): boolean {
        return !plgItemMenuItem || !orderType ||
            ((!plgItemMenuItem.orderTypes || !plgItemMenuItem.orderTypes.length || plgItemMenuItem.orderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType))) &&
                (!plgItemMenuItem.exclOrderTypes || !plgItemMenuItem.exclOrderTypes.length || !plgItemMenuItem.exclOrderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType))));
    }

    public plgItemIsAvailableForFilters(plgItemMenuItem: MenuPlgItemsMenuItemData, compositeMenuItem: MenuItemData, selectedFilterTags?: string): boolean {
        return !compositeMenuItem.filterPlgItems || (plgItemMenuItem && this.filterTagsMatch(plgItemMenuItem.filterTags, (compositeMenuItem.filterTags || "") + (selectedFilterTags || ""), plgItemMenuItem.includeOptionOnUntaggedItems));
    }

    public plgItemIsAvailable(plgItemMenuItem: MenuPlgItemsMenuItemData, compositeMenuItem: MenuItemData, selectedFilterTags?: string): boolean {
        return !(plgItemMenuItem && plgItemMenuItem.hidden) &&
            this.plgItemIsAvailableForStore(plgItemMenuItem) &&
            this.plgItemIsAvailableForMember(plgItemMenuItem) &&
            this.plgItemIsAvailableForOrderType(plgItemMenuItem) &&
            this.plgItemIsAvailableOnSchedules(plgItemMenuItem) &&
            this.plgItemIsAvailableForFilters(plgItemMenuItem, compositeMenuItem, selectedFilterTags);
    }

    public isProductLineQty(compositeItem: MenuQtyData): boolean {
        return compositeItem && compositeItem["productLine"] != undefined;
    }

    public isProductLinePlgQty(compositeItem: MenuQtyData): boolean {
        return compositeItem && compositeItem["plg"] != undefined;
    }

    /* ==================================================================
        MENU ITEM SCHEDULES
       ================================================================== */

    private hasSchedule(schedules: ScheduleData[]): boolean {
        return schedules && !!schedules.length;
    }

    private itemIsScheduled(menuItem: MenuItemData): boolean {
        return menuItem.mergedSchedules && menuItem.mergedSchedules.length &&
            menuItem.mergedSchedules.every((mergedSchedule: MergedScheduleData) =>
                this.hasSchedule(mergedSchedule.itemSchedules) ||
                this.hasSchedule(mergedSchedule.exclItemSchedules) ||
                this.hasSchedule(mergedSchedule.categorySchedules) ||
                this.hasSchedule(mergedSchedule.exclCategorySchedules) ||
                this.hasSchedule(mergedSchedule.primaryCategorySchedules) ||
                this.hasSchedule(mergedSchedule.exclPrimaryCategorySchedules) ||
                this.hasSchedule(mergedSchedule.submenuSchedules) ||
                this.hasSchedule(mergedSchedule.exclSubmenuSchedules));
    }

    private anyScheduleIsActive(schedules: ScheduleData[]): boolean {
        return !schedules || !schedules.length ||
            schedules.some((schedule: ScheduleData) => this.activeSchedulesSearchModel.data.some((activeSchedule: ScheduleData) => schedule.id == activeSchedule.id));
    }

    private noScheduleIsActive(schedules: ScheduleData[]): boolean {
        return !schedules || !schedules.length ||
            !schedules.some((schedule: ScheduleData) => this.activeSchedulesSearchModel.data.some((activeSchedule: ScheduleData) => schedule.id == activeSchedule.id));
    }

    private anyMergedScheduleIsActive(mergedSchedules: MergedScheduleData[]): boolean {
        return !mergedSchedules || !mergedSchedules.length ||
            mergedSchedules.some((mergedSchedule: MergedScheduleData) =>
                this.anyScheduleIsActive(mergedSchedule.itemSchedules) &&
                this.noScheduleIsActive(mergedSchedule.exclItemSchedules) &&
                this.anyScheduleIsActive(mergedSchedule.categorySchedules) &&
                this.noScheduleIsActive(mergedSchedule.exclCategorySchedules) &&
                this.anyScheduleIsActive(mergedSchedule.primaryCategorySchedules) &&
                this.noScheduleIsActive(mergedSchedule.exclPrimaryCategorySchedules) &&
                this.anyScheduleIsActive(mergedSchedule.submenuSchedules) &&
                this.noScheduleIsActive(mergedSchedule.exclSubmenuSchedules));
    }

    /* ==================================================================
        MENU ITEM SEARCH
       ================================================================== */

    private static stopWords: string[] = [
        "the", "a", "an", "and", "with", "on", "in", "of", "at", "for",
        "or", "to", "from", "by", "is", "are", "it", "that", "this", "which"
    ];

    private static synonyms: { [key: string]: string[] } = {
        "chips": ["fries", "wedges"],
        "fries": ["chips", "wedges"],
        "wedges": ["fries", "chips"],
        "capsicum": ["pepper"],
        "pepper": ["capsicum"]
    };

    private buildKeywordIndex(): void {
        this.categoryItems.forEach((menuItems: MenuItemData[], category: MenuCategoryData) =>
            menuItems.forEach((menuItem: MenuItemData) => {
                const itemInCategory: MenuItemInCategory = new MenuItemInCategory(menuItem, category);
                this.getItemKeywords(menuItem, category).forEach((weighting: number, keyWord: string) => {
                    let indexedMenuItems: IndexedMenuItem[] = this.menuItemKeywordIndex.get(keyWord);
                    if (!indexedMenuItems) {
                        indexedMenuItems = [];
                        this.menuItemKeywordIndex.set(keyWord, indexedMenuItems);
                    }
                    indexedMenuItems.push(new IndexedMenuItem(itemInCategory, weighting));
                });
            })
        );
    }

    private getItemKeywords(menuItem: MenuItemData, category: MenuCategoryData): Map<string, number> {
        const keyWords: Map<string, number> = new Map();

        menuItem.sellable.tags.map((tag: MenuSellableMetaTagDefinitionData) => tag.name)
            .forEach((tagName: string) => this.getKeywordsFromText(tagName).forEach((keyWord: string) => keyWords.set(keyWord, 1)));

        this.getKeywordsFromText(menuItem.sellable.primaryCategory.name).forEach((keyWord: string) => keyWords.set(keyWord, 2));
        menuItem.sellable.primaryCategory.ancestors.map((ancestor: MenuSellableCategoryLookupData) => ancestor.category.name)
            .forEach((categoryName: string) => this.getKeywordsFromText(categoryName).forEach((keyWord: string) => keyWords.set(keyWord, 2)));
        menuItem.sellable.customObjects.map((customObject: MenuSellableCustomObjectData) => customObject.name)
            .forEach((customObjectName: string) => this.getKeywordsFromText(customObjectName).forEach((keyWord: string) => keyWords.set(keyWord, 2)));

        menuItem.sellable.dietaryIndicators.map((indicator: MenuDietaryIndicatorsData) => indicator.name)
            .forEach((indicatorName: string) => this.getKeywordsFromText(indicatorName).forEach((keyWord: string) => keyWords.set(keyWord, 3)));
        this.getKeywordsFromText(category.name).forEach((keyWord: string) => keyWords.set(keyWord, 3));

        this.getKeywordsFromText(menuItem.name).forEach((keyWord: string) => keyWords.set(keyWord, 4));

        return keyWords;
    }

    private getKeywordsFromText(text: string): string[] {
        const intl: any = Intl; // old definition
        const segments = new intl.Segmenter([], {granularity: "word"}).segment(text.toLowerCase());
        return Array.from(segments)
            .filter((segment: any) => segment.isWordLike)
            .map((segment: any) => segment.segment)
            .filter((word: string) => word.length > 2 && MenuModel.stopWords.indexOf(word) < 0);
    }

    public searchItems(searchText: string): MenuItemInCategory[] {

        // Ignore any Levenshtein distance greater than this:
        const MAX_LEVENSHTEIN_DISTANCE: number = 2;

        const itemScores: Map<MenuItemInCategory, number> = new Map();

        // Extract the matchable words from search text entered by the user.
        searchText = searchText.trim();
        if (!searchText) {
            return [];
        }
        const searchWords: string[] = this.getKeywordsFromText(searchText);

        // Build the keyword map on demand to minimise overhead when building the menu.
        if (this.menuItemKeywordIndex.size == 0) {
            this.buildKeywordIndex();
        }

        // Iterate over every keyword (and its synonyms) looking for fuzzy matches.
        // A given keyword/synonym can match against a single search word. Find the best match.
        // Higher score is better:
        //   - Exact match = 5
        //   - Starts-with match = 3
        //   - Levenshtein distance 1 = 2
        //   - Levenshtein distance 2 = 1
        // Nothing else counts as a match.
        // The same item can accumulate scores by matching against multiple keywords associated with that item.
        // We sum the scores multiplied by the weighting (the weighting adjusts the score according to which properties of an item the keyword is derived from).
        this.menuItemKeywordIndex.forEach((indexedMenuItems: IndexedMenuItem[], keyWord: string) => {
            let bestScore: number = 0;
            [keyWord, ...MenuModel.synonyms[keyWord] || []].some((synonym: string) => searchWords.some((searchWord: string) => {
                const exactMatch: boolean = searchWord === synonym;
                bestScore = exactMatch ? MAX_LEVENSHTEIN_DISTANCE + 3 : Math.max(bestScore, synonym.startsWith(searchWord) ? (MAX_LEVENSHTEIN_DISTANCE + 1) : (MAX_LEVENSHTEIN_DISTANCE + 1 - levenshtein(searchWord, synonym)));
                return exactMatch;
            }));
            if (bestScore > 0) {
                indexedMenuItems.forEach((indexedMenuItem: IndexedMenuItem) => itemScores.set(indexedMenuItem.itemInCategory, (itemScores.get(indexedMenuItem.itemInCategory) || 0) + (bestScore * indexedMenuItem.weighting)));
            }
        });

        // Return a list of the matched items sorted by total score.
        return Array.from(itemScores, ([itemInCategory, score]: [MenuItemInCategory, number]) => ({
            itemInCategory,
            score
        }))
            .sort(({score: a}, {score: b}) => b - a)
            .map(({itemInCategory: itemInCategory}) => itemInCategory);
    }

    /* ==================================================================
        MENU NAVIGATION
       ================================================================== */

    private static readonly ANCHOR_BAR_POST_SELECT_AUTOSCROLL_SUPPRESSION_TIME_MS: number = 1000;

    public selectedCategorySubcategories: MenuCategoryData[] = [];
    public selectedCategoryAnchorNames: string[] = [];
    public selectedCategoryHasMainCategoryItems: boolean = false;

    private _selectedCategory: MenuCategoryData;
    private selectedCategoryName: string;
    private _selectedAnchorIndex: number = 0;
    private anchorSelectedTime: number = 0;

    public get selectedCategory(): MenuCategoryData {
        return this._selectedCategory;
    }

    public selectCategory(selectedCategory: MenuCategoryData, reselecting: boolean = false, event?: MouseEvent): void {
        const selectingSubCategory = this.isSubcategory(selectedCategory);
        this._selectedCategory = selectingSubCategory ? this.subToSuperCategories.get(selectedCategory) : selectedCategory;
        this.selectedCategoryName = this._selectedCategory ? this._selectedCategory.name : undefined;
        this._selectedAnchorIndex = undefined;
        if (!reselecting) {
            this.buildSelectedCategory();
        }
        MenuEvents.categorySelected$.next(new MenuCategorySelection(this._selectedCategory, reselecting));
        MenuEvents.menuBuilt$.next();

        if (selectingSubCategory) {
            setTimeout(() => this.selectAnchor(this.selectedCategorySubcategories.indexOf(selectedCategory) + (this.selectedCategoryHasMainCategoryItems ? 1 : 0), false, event), 500);
        }
    }

    private selectDefaultCategory(): void {
        if (!this._selectedCategory && this.selectableCategories) {
            this.selectCategory(this.selectableCategories[0]);
        }
    }

    private buildSelectedCategory(): void {
        this.selectedCategorySubcategories = [];
        this.selectedCategoryAnchorNames = [];
        this.selectedCategoryHasMainCategoryItems = false;

        if (this._selectedCategory) {
            this.selectedCategorySubcategories = this.isSuperCategory(this._selectedCategory) ? this.categories.filter((subcategory: MenuCategoryData) => this.isSubcategory(subcategory, this._selectedCategory) && this.categoryHasItems(subcategory)) : [];
            this.selectedCategoryAnchorNames = this.selectedCategorySubcategories.map((subcategory: MenuCategoryData) => this.getCategoryDisplayName(subcategory));

            // Add an extra anchor for the main category if it contains its own items
            this.selectedCategoryHasMainCategoryItems = this.categoryItems.has(this._selectedCategory) && !!this.categoryItems.get(this._selectedCategory).length;
            if (this.selectedCategoryHasMainCategoryItems && this.selectedCategorySubcategories.length) {
                this.selectedCategoryAnchorNames.unshift(this.getCategoryDisplayName(this._selectedCategory));
            }

            // Super-categories reflect the minimum wait time of their sub-categories
            if (this.selectedCategorySubcategories.length) {
                const subCategoriesMinWaitTime: number = Math.min(...(this.selectedCategorySubcategories.map((subcategory: MenuCategoryData) => subcategory.currentWaitTimeMinutes || 0)));
                this._selectedCategory.currentWaitTimeMinutes = this.selectedCategoryHasMainCategoryItems ? Math.min((this._selectedCategory.currentWaitTimeMinutes || 0), subCategoriesMinWaitTime) : subCategoriesMinWaitTime;
            }
        }
    }

    public selectAnchor(selectedAnchorIndex: number, scrollAnchorBarImmediately: boolean, event?: MouseEvent): void {
        if (selectedAnchorIndex === this._selectedAnchorIndex && !event) {
            return;
        }

        // If selecting due to a mouse event, record the time to prevent selection of intermediate anchors during item scrolling.
        if (event) {
            event.stopPropagation();
            this.anchorSelectedTime = Date.now();
        }

        // Even if no items intersections change, we need a final event to indicate that item scrolling has completed.
        if (!scrollAnchorBarImmediately) {
            MenuEvents.itemsScrolling$.next();
        }

        this._selectedAnchorIndex = selectedAnchorIndex;
        MenuEvents.anchorSelected$.next(!!event);
        if (scrollAnchorBarImmediately) {
            MenuEvents.scrollAnchorBar$.next(this._selectedAnchorIndex);
        }
    }

    public get selectedAnchorIndex(): number {
        return this._selectedAnchorIndex;
    }

    public get canAutoScrollAnchorBar() {
        return Date.now() - this.anchorSelectedTime >= MenuModel.ANCHOR_BAR_POST_SELECT_AUTOSCROLL_SUPPRESSION_TIME_MS;
    }
}



// WEBPACK FOOTER //
// ./src/app/menu/menu.model.ts